import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { cleanRankingsData, RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { getKey } from "@ugg/shared/api/data-parser/champions/common";

import { RankingStats } from "@ugg/shared/interfaces/champions/ranking-stats.interface";

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem ranking-stats override — self-contained module
// ─────────────────────────────────────────────────────────────────────
//
// ARAM Mayhem has its own data pipeline (see data_tierlists-html.py
// in ugg-data-digest) that produces a static champion tier list JSON
// on the CDN, keyed on numeric champion keys, rather than flowing
// through the normal ranking-stats backend. This module reshapes
// that data source into the RankingStats layout the rest of the
// site's champion consumers expect, so anything that reads a
// champion's tier / stdevs / rank (e.g. ChampionProfileHeader's
// badge) works unchanged under the ARAM Mayhem queue type.
//
// This hook is only intended to be called via the top-level
// dispatcher in ranking-stats.ts — direct callers would have to
// re-implement the queueType check themselves. The dispatcher
// gates on validatedQueryParams.queueType === QueueTypeS.ARAM_MAYHEM
// and skips this hook otherwise.

// Reverse-map from the tier string in the ARAM Mayhem JSON back to
// a stdevs value that will round-trip cleanly through getLuTier()
// in @ugg/shared/utils/tier-helpers. Values are chosen as the
// MIDPOINT of each bucket rather than the bucket floor for two
// reasons:
//   • "A" tier's floor is exactly 0, which the header treats as
//     falsy in `stdevs && rank && getLuTier(stdevs)` — hitting the
//     floor would silently suppress the badge.
//   • Any downstream code that applies its own tie-breaking around
//     bucket boundaries can nudge a champion into a neighboring
//     tier if we hand it a boundary value; midpoints buy safety
//     margin cheaply.
// "D" tier is open-ended below -0.75 so -1 is arbitrary but well
// clear of the boundary.
const ARAM_MAYHEM_TIER_TO_STDEVS: Record<"S+" | "S" | "A" | "B" | "C" | "D", number> = {
  "S+": 2.5,
  S: 1.375,
  A: 0.375,
  B: -0.25,
  C: -0.625,
  D: -1,
};

// Iteration order matches getLuTier's descending threshold ladder —
// a champion appears in exactly one tier bucket in the JSON, so the
// first match wins. Defined as a const tuple (not derived from the
// map above) so TypeScript keeps it in this specific order.
const ARAM_MAYHEM_TIER_ORDER = ["S+", "S", "A", "B", "C", "D"] as const;

// Shape of the ARAM Mayhem champion tier list JSON on the CDN
// (mirrors AramMayhemChampionTierList over in the tier-list
// container module). Duplicated here rather than imported to avoid
// coupling this backend module to the frontend feature package —
// the shape is small and hasn't churned since the data pipeline
// was written.
interface AramMayhemChampionTierListJson {
  tiers: Record<"S+" | "S" | "A" | "B" | "C" | "D", number[]>;
  lastUpdated: string;
}

// Builds the CLEANED ranking-stats shape for a single champion in
// ARAM Mayhem — emits `Record<string, RankingStatsInfo>` with a
// single entry at the same composite key `getRankings` will look
// up, matching what `cleanRankingsData` would produce for a normal
// fetch. Only fields ChampionProfileHeader actually reads (via
// getRankings) get non-zero values:
//   • stdevs — reverse-mapped from the tier string
//   • rank   — 1 when the champion is in a tier, 0 otherwise, so
//              the header's `stdevs && rank` truthy check works
// Everything else is 0 / empty / "0.0" — ARAM Mayhem has no
// per-role win rates, bans, damage-per-game, counters, etc., and
// none of it is surfaced by any consumer of this hook today.
//
// The reason we build the CLEANED shape directly rather than
// routing through cleanRankingsData:
//   1. cleanRankingsData expects `Record<RegionN, Record<RankN,
//      Record<RoleN, ChampionStats>>>` with NUMERIC enum keys at
//      each nesting level (Object.keys → .map(Number)) — string
//      keys like "world" become NaN and the whole loop iterates
//      over [NaN] and produces {}.
//   2. Even if the keys typed correctly, the derived fields
//      (`avg_damage: damage / roleMatches`, `pick_rate:
//      (roleMatches / total_matches) * 100`, ...) would all
//      divide by 0 and land in the RankingStatsInfo as NaN /
//      Infinity. The header doesn't read those, but shipping
//      NaN in shared data is a footgun waiting for the next
//      consumer.
function buildAramMayhemRankingsData(
  json: unknown,
  championId: number,
  validatedQueryParams: Record<string, any>,
): Record<string, RankingStatsInfo> {
  const tiers =
    (json as AramMayhemChampionTierListJson | null | undefined)?.tiers ?? ({} as AramMayhemChampionTierListJson["tiers"]);

  let matchedTier: (typeof ARAM_MAYHEM_TIER_ORDER)[number] | undefined;
  for (const tier of ARAM_MAYHEM_TIER_ORDER) {
    const bucket = tiers[tier];
    if (Array.isArray(bucket) && bucket.includes(championId)) {
      matchedTier = tier;
      break;
    }
  }

  // Missing champion → stdevs=0, rank=0. Both fall through the
  // header's `!stdevs || !rank` guard to the neutral #414165
  // badge, matching how the header handles truly missing data.
  const stdevs = matchedTier !== undefined ? ARAM_MAYHEM_TIER_TO_STDEVS[matchedTier] : 0;
  const rank = matchedTier !== undefined ? 1 : 0;

  const info: RankingStatsInfo = {
    roleWins: 0,
    roleMatches: 0,
    rank, // synthetic — passes the header's truthy check when the champion has a tier
    total_rank: 0,
    avg_damage: 0,
    avg_gold: 0,
    avg_kda: "0.0", // string, not number — see the calculateKDA callsite in cleanRankingsData
    avg_cs: 0,
    win_rate: 0,
    pick_rate: 0,
    ban_rate: 0,
    bans: 0,
    total_matches: 0,
    counters: [],
    real_matches: 0,
    stdevs, // synthetic — the whole point of this module
    effective_winrate: 0,
    distribution_count: 0,
    distribution_mean: 0,
    distribution_stdevs: 0,
    be_all_picks: 0,
  };

  // Match getData's ARAM Mayhem normalization (see common.ts:
  // getData force-sets rank=RankS.OVERALL, role=RoleS.NONE for
  // ARAM_MAYHEM regardless of caller params, so the composite key
  // it looks up is always the normalized form). Our synthetic
  // entry has to live at that same key or the lookup misses.
  //
  // Can't read these off validatedQueryParams reliably — the
  // hook's validateQueryParams pass strips `role` (roles aren't a
  // query param on the OVERVIEW page, they come from
  // ChampionProfileContext), so validatedQueryParams.role comes
  // back undefined even when the caller passes role: "none" in
  // params. Hard-coding the two normalized string values matches
  // getData exactly; if those constants ever drift, common.ts
  // and this file need to change in lockstep.
  const key = getKey(
    validatedQueryParams.region as string,
    "overall", // RankS.OVERALL — getData forces this for ARAM Mayhem
    "none", // RoleS.NONE   — getData forces this for ARAM Mayhem
  );

  return { [key]: info };
}

// ARAM Mayhem-specific ranking-stats hook. Same signature as
// useChampionRankingStatsOriginal so the top-level dispatcher can
// hand off transparently — same options, same return shape.
//
// Fetch source: the same tier-list JSON useAramMayhemChampionTierList
// consumes on the tier-list page. JSONFetcher's underlying HTTP
// request is deduped by URL at the network level even when cache
// keys differ, so navigating between champion profiles or between
// the tier-list page and a champion profile stays near-zero cost
// after the first fetch.
export function useChampionRankingStatsAramMayhem(
  championId: number,
  options?: { params?: Record<string, any> } & JSONFetcherOptions<RankingStats, ReturnType<typeof cleanRankingsData>>,
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);
  const { patch } = validatedQueryParams;

  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-champions-${patch}.json`;

  // championId is spliced into the cache key because the URL is
  // SHARED across every champion in a given patch (unlike the
  // normal ranking-stats URL, which encodes championId in the
  // path). Without it, whichever champion loaded first wins the
  // cache slot and every subsequent champion pulls back that first
  // champion's derived data — surfaces as "every champion shows
  // the same tier." Per-champion cache slots do cost a small
  // amount of JS memory (one RankingStatsInfo object per visited
  // champion), but the underlying HTTP request is deduped by the
  // browser's own network cache, so the wall-clock cost stays
  // near-zero after the first hit.
  //
  // The RANKING_STATS_ARAM_MAYHEM prefix also namespaces this
  // cache slot away from useAramMayhemChampionTierList, which
  // fetches the same URL but transforms the payload with a
  // different cleaner ({tierlist, lastUpdated} instead of the
  // flat RankingStatsInfo dict). Without the prefix, both hooks
  // would fight over the same slot and whichever wrote last
  // wins — surfacing as a shape mismatch in getRankings
  // downstream.
  const cacheKey = `RANKING_STATS_ARAM_MAYHEM::${championId}::${url}`;

  // Skip the fetch when we don't have a patch — the URL would be
  // malformed and the CDN would 404. Falls through to the
  // caller-supplied `skip` so existing skip behavior is preserved.
  const effectiveSkip = skip || !patch;

  return useJSONFetcher<RankingStats, ReturnType<typeof cleanRankingsData>>(url, {
    ssr,
    skip: effectiveSkip,
    onCompleted: (url, json, _cacheKey) => {
      // Emit the cleaned shape DIRECTLY — we can't route through
      // cleanRankingsData because (1) it expects numeric enum
      // keys at each nesting level, (2) every derived field
      // would divide by roleMatches=0 and produce NaN. See the
      // buildAramMayhemRankingsData docstring for the full story.
      return buildAramMayhemRankingsData(json, championId, validatedQueryParams);
    },
    customCacheKey: cacheKey,
  });
}
